//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  CardHeader,
} from '@mui/material';

// === COMPONENTS ===
import { Document } from './Document/index.js';
import { Block    } from './Block/index.js';
import { FieldPanelProvider } from './FieldPanelContext/FieldPanelContext.jsx';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

const DocMemo   = React.memo( Document );
const BlockMemo = React.memo( Block    );

//-----------------------------------------------------------------
export function Fields(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();
  const ref   = useRef();

  const [    folder, setFolder    ] = useState( null );
  const [ documents, setDocuments ] = useState( { items: {}, order: [] } );

  //#endregion

  //#region [STYLES]
  
  const _styles = {
    field_paper: {
      gridColumn : 'span ' + props.fill || 4,
      overflow   : 'auto'     ,
      position   : 'relative' ,
      height     : '100%'     ,
      width      : '100%'     ,
      boxShadow  : 0          ,
    },
    document_container: {
      height: '100%', width: '100%',
    },
    container: {
      variant  : 'primary',
      borderRadius: '0.5rem',
      boxShadow: '2',
      width: '100%',
      margin: 0,
      marginBottom: '1rem',
    },
    header: {
      boxShadow: '-0.5em 0  0.01em' + theme.palette.success.main,
      padding: '1rem',
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      height: '4rem',
      textAlign: 'center',
      zIndex: 5,
    },
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFolder(data) {
      //-----------------------------------------------------------------
      setFolder( data );
    }

    //-----------------------------------------------------------------
    function handleDocuments(data) {
      //-----------------------------------------------------------------
      setDocuments( data );
    }

    //-----------------------------------------------------------------
    function handleClear(data) {
      //-----------------------------------------------------------------
      setFolder   ( null );
      setDocuments( { items: {}, order: [] } );
    }

    on( 'TASK_FOLDER'    , handleFolder    );
    on( 'TASK_DOCUMENTS' , handleDocuments );
    on( 'FIELDS_CLEAR'   , handleClear     );

    return () => {
      removeListener( 'TASK_FOLDER'     , handleFolder    );
      removeListener( 'TASK_DOCUMENTS'  , handleDocuments );
      removeListener( 'FIELDS_CLEAR'    , handleClear     );
    }
  }, [] );
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData( 'TASK_VIEW_FIELDS_UNSUBSCRIBE' );
    }

    sendMessageData( 'TASK_VIEW_FIELDS_SUBSCRIBING' );

    window.addEventListener('beforeunload', unsubscribe );
    return () => {
      window.removeEventListener('beforeunload', unsubscribe );
    }
  }, [] );

  useEffect( () => {
    function _handleReconnect(data) {
      sendMessageData( 'TASK_VIEW_FIELDS_SUBSCRIBING', data );
    }

    on( 'connection', _handleReconnect );

    return () => {
      removeListener( 'connection', _handleReconnect );
    }
  }, [] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleDocumentUpdates( data ) {
      //-----------------------------------------------------------------
      try {
        const temp = { ...documents };

        // === DOCUMENTS
        if( data.documents?.order ) temp.order = data.documents.order;

        data.documents?.create?.forEach( document => {
          temp.items[ document.Id ] = document;
        });
        data.documents?.update?.forEach( document => {
          document.timestamp = Date.now();
          temp.items[ document.Id ] = document;
        });
        data.documents?.remove?.forEach( id => {   
          delete temp.items[ id ];
        });
        setDocuments( temp );
      } catch (error) {
        console.warn( error );
      }
    }

    on( 'DOCUMENT_UPDATE' , handleDocumentUpdates );

    return () => {
      removeListener( 'DOCUMENT_UPDATE' , handleDocumentUpdates );
    }
  }, [documents] );

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( !folder || !documents ) return null;

    const fields_folder    = folder   .blocks.map( displayFolderBlock );
    const fields_documents = documents.order .map( displayDocument    );

    let body = null;
    if( fields_folder.length <= 0 && fields_documents.length <= 0 ) {
      body = (
        <Box ref={ref} sx={_styles.field_paper}>
          <Box ref={ref} sx={_styles.container} >
            <CardHeader sx={_styles.header} title='No validation required.' />
          </Box>
        </Box>
      )
    } else {
      body = (
        <Box ref={ref} sx={_styles.field_paper}>
          { fields_folder }
          <Box sx={_styles.document_container}>
            { fields_documents }
          </Box>
        </Box>
      );
    }

    return (
      <FieldPanelProvider>
        { body }
      </FieldPanelProvider>
    )
  }

  //-----------------------------------------------------------------
  function displayFolderBlock( id ) {
    //-----------------------------------------------------------------
    const timestamp = folder?.timestamp;
    return <BlockMemo key={ 'f' + id } id={ id } timestamp={timestamp}/>
  }

  //-----------------------------------------------------------------
  function displayDocument( id ) {
    //-----------------------------------------------------------------
    const document = documents.items[id];
    if( !document ) {
      console.log( `Failed to get document with id [${id}].`);
    }
    const timestamp = document?.timestamp;
    return <DocMemo key={ 'd' + id } id={ id } timestamp={timestamp}/>
  }

  //#endregion

  return display();
}